<template>
  <v-container
    fluid
    tag="section"
    class="px-0 px-md-3 parcel-card-container"
  >
    <v-card 
      class="parcel-card"
      :class="{ 'active-card': active }"
      :disabled="!active" 
      rounded="lg"
      elevation="3"
    >
      <!-- Parcel Header -->
      <v-card-title 
        class="parcel-header"
        :style="storeColorStyle"
      >
        <div class="d-flex align-center">
          <v-icon color="primary" class="mr-2">mdi-package-variant</v-icon>
          <span class="text-h6 font-weight-medium parcel-title">
            <span translate="no">{{ index | LetterFilter }}</span>: {{ order.name }} - {{ order.shipping_address.name }}
          </span>
        </div>
        
        <!-- Reload Button - Absolute positioned in top right -->
        <v-btn
          icon
          small
          class="reload-button"
          @click="$emit('refetch', index)"
        >
          <v-icon>mdi-reload</v-icon>
        </v-btn>
      </v-card-title>
      
      <v-divider></v-divider>
      
      <!-- Barcode Scanner -->
      <transition name="slide">
        <div v-if="request_barcode_scan" class="scanner-section">
          <BarcodeScanner 
            v-if="!uses_barcode_scanner"
            :key="scanner_mount_key"
            class="barcode-scanner"
            @loaded="on_scanner_loaded"
            @decode="on_scanned_barcode" 
          />
          
          <v-text-field 
            ref="barcodeInput"
            v-model="barcode"
            dense
            hide-details
            outlined
            rounded
            label="Barcode"
            v-if="uses_barcode_scanner"
            inputmode="none"
            autocomplete="off"
            autocorrect="off"
            autocapitalize="off"
            spellcheck="false"
            @input="on_barcode_input"
            @keydown.enter.prevent="submit_barcode"
            @keydown.tab.prevent="submit_barcode"
            @focus="prepare_barcode_input"
            @blur="focus_barcode_input"
            prepend-inner-icon="mdi-barcode-scan"
            class="barcode-input"
          />
          <v-alert
            v-if="show_scanner_help"
            type="warning"
            outlined
            dense
            class="mt-2 mb-0"
          >
            Camera did not initialize. Switch to scanner mode to use a hardware scanner instead.
          </v-alert>
        </div>
      </transition>
      
      <!-- Order Notes -->
      <v-card-text class="parcel-content">
        <!-- Order Note -->
        <v-alert
          v-if="order.note"
          outlined
          dense
          prominent
          icon="mdi-note-text"
          class="order-note mb-4"
        >
          <span>{{ order.note }}</span>
        </v-alert>
        
        <!-- Customs id number (Brazil CPF, China resident id...) fetched from Shopify's localized fields -->
        <v-chip
          v-if="order.sf_customs_identity && order.sf_customs_identity.value"
          color="secondary"
          outlined
          class="mb-4 instruction-chip"
        >
          <v-icon left small>mdi-card-account-details</v-icon>
          {{ order.sf_customs_identity.label }} {{ order.sf_customs_identity.value }} is printed on the label
        </v-chip>
        <v-alert
          v-else-if="customs_identity_label"
          type="warning"
          outlined
          dense
          prominent
          icon="mdi-alert-circle"
          class="mb-4"
        >
          <div>
            <strong>Important:</strong> This order is going to {{ order.shipping_address.country }}. A {{ customs_identity_label }} number is required for customs but was not found on the order. Add it to the parcel manually, or block the order.
            <ToolTip>Shopify only collects the {{ customs_identity_label }} at checkout when the field is enabled for {{ order.shipping_address.country }} under Settings → Markets in the store. Once enabled, Fulfillment Hero fetches it and prints it on the label automatically.</ToolTip>
          </div>
        </v-alert>
        
        <!-- Parcel Errors -->
        <ParcelErrorCard
          v-for="(error, i) in (order.errors || [])"
          :key="`error-${i}`"
          :order="order"
          :index="index"
          :error="error"
          @retry="$emit('retry')"
          class="mb-4"
        />
        
        <!-- Shipping Method Selection -->
        <!-- Quick Select for existing mapped methods -->
        <ShippingQuickSelect
          v-if="(show_manual_shipment && !store.uses_shopify_labels)"
          :order="order"
          @save="set_method"
          class="mb-4"
        />
        
        <ShippingMapCard 
          v-if="(show_manual_shipment && !store.uses_shopify_labels)"
          :preset_country="order.shipping_address.country_code"
          :preset_consignee="{ countryCode: order.shipping_address.country_code, postalCode: order.shipping_address.zip  }"
          @save="set_method"
          class="mb-4"
        />
        
        <ShopifyLabelsCard
          v-else-if="show_manual_shipment"
          :order="order"
          :store="store"
          @save="set_method"
          class="mb-4"
        />
        
        <!-- Manual label generation -->
        <div v-if="show_generate_label" class="text-center mb-4">
          <v-btn
            color="primary"
            rounded
            outlined
            @click="$emit('generate_label', index)"
          >
            <v-icon left>mdi-label</v-icon>
            Generate label
          </v-btn>
        </div>
        
        <!-- Labels Section -->
        <div
          v-if="order.sf_data.labels && !loading"
          class="labels-section mb-4"
        >
          <div class="text-subtitle-2 font-weight-medium mb-2">
            <v-icon color="primary" class="mr-1">mdi-label</v-icon>
            Labels
          </div>
          
          <div class="labels-grid">
            <v-menu 
              offset-y
              v-for="(label, i) in order.sf_data.labels"
              :key="`print-${i}`"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  small
                  rounded
                  outlined
                  color="primary"
                  class="label-btn"
                  v-bind="attrs"
                  v-on="on"
                >
                  <template v-if="label.description.toLowerCase().includes('label')">
                    <span class="tracking-number">{{ label.tracking_number ? '#' : '' }}{{ label.tracking_number || 'Label' }}</span>
                  </template>
                  <template v-else>{{ label.description === 'CUSTOMS_DOC' ? 'Customs docs' : label.description }}</template>
                  <v-progress-circular
                      v-if="isPrintingThisParcel"
                      indeterminate
                      size="18"
                      class="ml-2"
                      width="2"
                      color="primary"
                    ></v-progress-circular>
                  <v-icon v-else small right>mdi-chevron-down</v-icon>
                </v-btn>
              </template>
              
              <v-list nav dense rounded>
                <v-list-item
                  @click="sendToPrint({pdf_ref: label.ref, description: label.description, media: label.media, })"
                  v-if="company.printserver_id"
                  :disabled="!get_printing_possible || !get_printserver_connected || isPrintingThisParcel"
                >
                  <v-list-item-icon>
                    <v-icon>mdi-printer-wireless</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    Resend to printer
                  </v-list-item-content>
                </v-list-item>
                
                <v-list-item @click="openPrint(label.ref)">
                  <v-list-item-icon>
                    <v-icon>mdi-open-in-new</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>Open in new tab</v-list-item-content>
                </v-list-item>
                
                <v-list-item @click="printWithDialog(label.ref)">
                  <v-list-item-icon>
                    <v-icon>mdi-printer</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>Print</v-list-item-content>
                </v-list-item>

                <v-list-item
                  v-if="can_delete_label(label)"
                  :disabled="deleting_label"
                  @click="delete_label(label)"
                >
                  <v-list-item-icon>
                    <v-icon color="error">mdi-delete</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>Delete label</v-list-item-content>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
          <div>
            <ToolTip v-if="order.sf_data.labels.some(label => label.missing_signature)">
              <span class="text-caption">Add your signature in Settings > Users and it will automatically print on the labels</span>
            </ToolTip>
        </div>
        </div>
        
        <!-- Loading State -->
        <div v-else-if="loading && !(order.sf_data.labels || []).length" class="text-center py-4">
          <v-progress-circular
            indeterminate
            color="primary"
            size="32"
            width="3"
          ></v-progress-circular>
          <div class="text-caption grey--text text--darken-1 mt-2">Generating label...</div>
        </div>
        
        <!-- Special Instructions -->
        <div v-if="company.custom_scripts.some(i => i === 'avatar')" class="special-instructions mb-4">
          <v-chip
            v-if="order.sf_data_source_id === '98ca94188af' && order.line_items.some(a => a.product_id === 4297505767517 || a.product_id === 4297503735901)"
            color="secondary"
            outlined
            class="instruction-chip"
          >
            <v-icon left small>mdi-sticker</v-icon>
            {{ new Date().getFullYear() }} Citizen sticker
          </v-chip>
        </div>
        
        <v-chip
          v-if="order.sf_data.shipping_service && ((order.sf_data.shipping_service.addons || []).some(a => a.id === 'SF_STAMPS') || order.sf_data.shipping_service.service_id === 'UX')"
          color="secondary"
          outlined
          class="mb-4 instruction-chip"
        >
          <v-icon left small>mdi-stamp</v-icon>
          Add {{ amount_stamps }}x stamps
        </v-chip>
        
        <!-- Packing Instructions -->
        <div v-if="store && activePackingInstructions.length > 0" class="packing-instructions-section mb-4">
          <div class="text-subtitle-2 font-weight-medium mb-2">
            <v-icon color="primary" class="mr-1">mdi-clipboard-text</v-icon>
            Packing Instructions
          </div>
          
          <v-card outlined rounded="lg" class="packing-instructions-card">
            <v-card-text class="py-2">
              <v-list dense>
                <v-list-item v-for="(instruction, index) in activePackingInstructions" :key="index">
                  <v-list-item-icon>
                    <v-icon small color="primary">{{ 'mdi-chevron-right'}}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ instruction.text }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-card-text>
          </v-card>
        </div>
        
        <!-- Item List -->
        <div class="items-section mb-4">
          <div class="text-subtitle-2 font-weight-medium mb-2">
            <v-icon color="primary" class="mr-1">mdi-package-variant-closed</v-icon>
            Items
          </div>
          
          <v-card outlined rounded="lg" class="items-card">
            <v-list dense>
              <v-list-item 
                v-for="(item, item_i) in order.line_items.filter(item => item.fulfillable_quantity)" 
                :key="`item-${item_i}`" 
                :class="{'text-decoration-line-through': !item.fulfilled_quantity}"
              >
                <v-list-item-action v-if="get_product(item.uuid) && get_product(item.uuid).barcode && company.require_item_scanning && item.fulfilled_quantity">
                  <v-btn
                    icon
                    small
                    :color="item.is_scanned ? 'success' : 'grey'"
                    @mousedown.prevent
                    @click="prompt_manual_scan(item)"
                  >
                    <v-icon>mdi-barcode-scan</v-icon>
                  </v-btn>
                </v-list-item-action>
                
                <v-list-item-content>
                  <v-list-item-title class="item__title">
                    <span v-if="item.removed_quantity && item.fulfilled_quantity" class="text-decoration-line-through mr-2">{{ item.fulfillable_quantity }}</span>
                    <strong>{{ item.fulfilled_quantity }}x</strong> {{ get_product_title(item.uuid) || item.name }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card>
        </div>
        
        <!-- Parcel Photo -->
        <ParcelPhoto 
          v-if="!request_barcode_scan && active && company.require_fulfillment_photos"
          :order_index="index"
          class="mb-4"
        />
      </v-card-text>
      
      <!-- Action Buttons -->
      <v-card-actions class="parcel-actions" v-if="!isShipmentLimited">
        <v-spacer></v-spacer>
        <v-btn 
          color="success" 
          rounded 
          elevation="2"
          x-large
          :loading="loading"
          @click="$emit('done', { seconds_spent: seconds_spent, index })"
          :disabled="!enableFulfillment"  
          class="action-button px-4"
        >
          <v-icon left>mdi-check</v-icon>
          Complete
        </v-btn>
        <v-spacer></v-spacer>
      </v-card-actions>
      
      <div class="text-center pb-4" v-if="!isShipmentLimited && ((active && !loading) || force_skip)">
        <v-btn 
          color="warning" 
          outlined
          rounded 
          @click="$emit('drop')"
          class="skip-button"
        >
          <v-icon left>mdi-package-variant-remove</v-icon>
          {{ order.sf_data.shipping_service && order.sf_data.shipping_service.type === 'shopify' && order.sf_data.labels && order.sf_data.labels.length ? 'Void label and skip order' : 'Skip without fulfilling' }}
        </v-btn>
      </div>
      <v-alert
        v-else-if="isShipmentLimited"
        type="warning"
        outlined
        dense
        class="ma-4"
      >
        {{ shipmentLimitMessage }}
        <router-link to="/auth/plans">Upgrade plan</router-link> to continue fulfilling orders.
      </v-alert>
    </v-card>
  </v-container>
</template>

<script>
import { LetterFilter } from '@/utils/filters'
import { normalize_barcode, barcodes_match } from '@/utils/barcode'
import { is_label_deletable } from '@/utils/labels'
import { mapState, mapGetters } from 'vuex'
import ShippingMapCard from '@/components/ShippingMapCard'
import ShippingQuickSelect from '@/components/ShippingQuickSelect'
import BarcodeScanner from '@/components/BarcodeScanner.vue'
import print from 'print-js'
import ParcelPhoto from '@/components/ParcelPhoto'
import ShopifyLabelsCard from '@/components/ShopifyLabelsCard'
import ParcelErrorCard from '@/components/ParcelErrorCard.vue';
import ToolTip from '@/components/ToolTip'
import { getStorage, ref as storageRef, getDownloadURL } from 'firebase/storage'

export default {
  components: {
    ShippingMapCard,
    ShippingQuickSelect,
    BarcodeScanner,
    ParcelPhoto,
    ShopifyLabelsCard,
    ParcelErrorCard,
    ToolTip,
  },
  props: {
    order: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
      required: true, 
      default: 0,
    },
    active: {
      type: Boolean, 
      required: true,
      default: false,
    },
    loading: {
      type: Boolean,
      required: true,
      default: false,
    },
    show_manual_shipment: {
      type: Boolean, 
      required: true,
      default: false,
    },
    force_skip: {
      type: Boolean,
      required: false,
      default: false,
    }
  },
  watch: {
    active: {
      handler: function(active){
        if(!active)return
        if(!this.timer) this.timer = setInterval(() => {
          if(!this.active || !document.hasFocus()) return 
          this.seconds_spent += 1
        }, 1000)
      },  
      immediate: true,
    },
    carrier: function(id){
      if(id && !this.shipping.carriers[id]) this.$store.dispatch('shipping/get_carrier_partners', {carrier_id: id})
    },
    show_barcode_input: {
      handler: function(show){
        if(show && this.uses_barcode_scanner) this.$nextTick(() => this.focus_barcode_input())
      }
    },
    request_barcode_scan: {
      handler: function(shouldScan){
        if(shouldScan){
          this.scanner_loaded = false
          this.show_scanner_help = false
          this.show_barcode_input = true
          if(this.uses_barcode_scanner) {
            if(this.scanner_health_timeout) clearTimeout(this.scanner_health_timeout)
            this.scanner_health_timeout = null
            this.$nextTick(() => this.focus_barcode_input())
          } else {
            this.scanner_mount_key += 1
            this.schedule_scanner_health_check()
          }
        } else {
          this.barcode = ''
          this.scanner_loaded = false
          this.show_scanner_help = false
          if(this.scanner_health_timeout) clearTimeout(this.scanner_health_timeout)
          this.scanner_health_timeout = null
        }
      }
    },
    uses_barcode_scanner: {
      handler: function(usesScanner){
        this.barcode = ''
        this.show_scanner_help = false
        if(this.scanner_health_timeout) clearTimeout(this.scanner_health_timeout)
        this.scanner_health_timeout = null

        if(!this.request_barcode_scan) return
        if(usesScanner) {
          this.$nextTick(() => this.focus_barcode_input())
          return
        }

        this.scanner_loaded = false
        this.scanner_mount_key += 1
        this.schedule_scanner_health_check()
      }
    }
  },
  computed: {
    // Mirrors CUSTOMS_IDENTITY_COUNTRIES in functions/modules/common.js, for parcels bucketed
    // before the number was fetched (or when the fetch failed) so the warning still shows
    customs_identity_label() {
      if (this.order.sf_customs_identity) return this.order.sf_customs_identity.label
      return { BR: 'CPF', CN: 'Resident ID', KR: 'PCCC' }[this.order.shipping_address && this.order.shipping_address.country_code] || null
    },
    ...mapState({
      company: (state) => state.warehouse.company,
      shipping: state => state.shipping,
      photos: state => state.order.photos,
      printing_parcel_index: (state) => state.warehouse.printing_parcel_index,
      shipments_last_30_days: state => state.order.shipments_last_30_days,
    }),
    ...mapGetters({
      // get_parcel_errors: 'order/get_parcel_errors',
      get_available_methods: 'shipping/get_available_methods',
      get_printing_possible: 'warehouse/get_printing_possible',
      get_label_generation_mode: 'warehouse/get_label_generation_mode',
      get_printserver_connected: 'warehouse/get_printserver_connected',
      get_product: 'product/get_product',
      get_product_title: 'warehouse/get_product_title',
      get_plan: 'warehouse/get_plan',
    }),
    store(){
      return this.company.data_sources.find(i => i.id === this.order.sf_data_source_id)
    },
    carrier_account(){
      const carrier_id = this.order.sf_data.shipping_service?.sf_carrier_account_id
      return carrier_id ? (this.company.carriers || []).find(c => c.id === carrier_id) : null
    },
    activePackingInstructions() {
      if (!this.store || !this.store.packing_instructions) return [];
      
      const instructions = this.store.packing_instructions || [];  
        // Filter instructions based on conditions
        return instructions.filter(instruction => {
          // If no condition, always show
          if (!instruction.condition) return true;
          
          // Check if condition is met
          if (instruction.condition.type === 'parcel-contains-tag') {
            // Check if any product in the order has the specified tag
            const tag = instruction.condition.key;
            return this.order.line_items.some(item => {
              const product = this.get_product(item.uuid);
              console.log(product)
              return product && product.tags && product.tags.includes(tag);
            });
          }
          
          return true;
        });
      
    },
    request_barcode_scan(){
      return this.company.require_item_scanning
        && this.order.line_items.some(item => !item.is_scanned && this.get_product(item.uuid) && this.get_product(item.uuid).barcode && item.fulfilled_quantity && this.active)
    },
    uses_barcode_scanner(){
      return this.company.uses_barcode_scanner === true
    },
    has_camera_scan_issue(){
      return this.show_scanner_help && !this.scanner_loaded
    },
    planShipmentLimit(){
      const limit = this.get_plan?.max_monthly_shipments
      return Number.isFinite(limit) ? limit : null
    },
    isShipmentLimited(){
      return !!this.planShipmentLimit && this.shipments_last_30_days >= this.planShipmentLimit
    },
    shipmentLimitMessage(){
      if(!this.planShipmentLimit) return ''
      return `Plan limit reached: ${this.shipments_last_30_days}/${this.planShipmentLimit} shipments this month.`
    },
    enableFulfillment(){
      if(this.isShipmentLimited) return false
      // this.$store.commit('app/SET_SNACK_BAR', this.request_barcode_scan + ' ' + 
      // this.force_skip + ' ' + 
      // this.get_printing_possible + ' ' + 
      // this.company.require_fulfillment_photos + ' ' + 
      // (this.order.sf_data.labels && this.order.sf_data.labels.length))
      if(this.request_barcode_scan && !this.has_camera_scan_issue) return false
      if(this.force_skip) return false
      if(!this.get_printing_possible) return true
      if(this.company.require_fulfillment_photos && (!this.photos[this.index] || !this.photos[this.index].length)) return false
      return this.order.sf_data.labels && this.order.sf_data.labels.length
    },
    amount_stamps(){
      const breaks = {
        50: { SE: 1, ROW: 2},
        100: { SE: 2, ROW: 3},
        250: { SE: 3, ROW: 6},
        500: { SE: 4, ROW: 8},
        1000: { SE: 6, ROW: 11},
        2000: { SE: 7, ROW: 13},
      }
      for(const i in breaks){
        if(this.order.total_weight < parseInt(i)) return this.order.shipping_address.country_code === 'SE' ? breaks[i].SE : breaks[i].ROW
      }
      return '?'
    },
    storeColorStyle() {
      if (this.store && this.store.color_identifier) {
        // Add 4D to the hex color for 0.3 opacity (30% in hex is 4D)
        return { 'background-color': `${this.store.color_identifier}` };
      }
      return {  }; // Default background color
    },
    isPrintingThisParcel() {
      return this.printing_parcel_index === this.index;
    },
    show_generate_label(){
      return this.get_label_generation_mode === 'manual'
        && this.active
        && !this.loading
        && !this.force_skip
        && !this.show_manual_shipment
        && this.get_printing_possible
        && !!this.order.sf_data.shipping_service
        && !(this.order.sf_data.labels || []).length
        && !(this.order.errors || []).length
    },
  },
  filters: {
    LetterFilter,
  },
  data() {
    return {
      carrier: null,
      service: null,
      loading_manual_shipment: false,
      barcode: '',
      show_barcode_input: false,
      scanner_mount_key: 0,
      scanner_loaded: false,
      show_scanner_help: false,
      scanner_health_timeout: null,
      barcode_input_timeout: null,
      manual_scan_prompt_open: false,
      seconds_spent: 0,
      timer: null,
      deleting_label: false,
    }
  },
  methods: {
    schedule_scanner_health_check(){
      if(this.scanner_health_timeout) clearTimeout(this.scanner_health_timeout)
      this.scanner_health_timeout = setTimeout(() => {
        if(this.request_barcode_scan && !this.scanner_loaded) this.show_scanner_help = true
      }, 5000)
    },
    on_scanner_loaded(){
      this.scanner_loaded = true
      this.show_scanner_help = false
      if(this.scanner_health_timeout) clearTimeout(this.scanner_health_timeout)
      this.scanner_health_timeout = null
      this.show_barcode_input = true
      if(this.uses_barcode_scanner) this.$nextTick(() => this.focus_barcode_input())
    },
    get_barcode_input_element(){
      const input = this.$refs.barcodeInput
      return input && input.$el ? input.$el.querySelector('input') : null
    },
    prepare_barcode_input(){
      if(this.manual_scan_prompt_open) return
      if(!this.request_barcode_scan || !this.uses_barcode_scanner) return
      const inputElement = this.get_barcode_input_element()
      if(!inputElement) return

      inputElement.setAttribute('inputmode', 'none')
      inputElement.setAttribute('autocomplete', 'off')
      inputElement.setAttribute('autocorrect', 'off')
      inputElement.setAttribute('autocapitalize', 'off')
      inputElement.setAttribute('spellcheck', 'false')
      inputElement.setAttribute('virtualkeyboardpolicy', 'manual')
      if(navigator.virtualKeyboard && typeof navigator.virtualKeyboard.hide === 'function') {
        navigator.virtualKeyboard.hide()
      }
    },
    focus_barcode_input(){
      if(this.manual_scan_prompt_open) return
      if(!this.request_barcode_scan || !this.uses_barcode_scanner) return
      const input = this.$refs.barcodeInput
      this.prepare_barcode_input()
      if(input && typeof input.focus === 'function') input.focus()
      const inputElement = this.get_barcode_input_element()
      if(inputElement && document.activeElement !== inputElement) inputElement.focus({ preventScroll: true })
    },
    on_hardware_scanner_keydown(event){
      if(!this.active || !this.request_barcode_scan || !this.uses_barcode_scanner) return
      if(this.manual_scan_prompt_open) return
      if(event.metaKey || event.ctrlKey || event.altKey) return

      if(event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault()
        this.submit_barcode({ force: true })
        return
      }

      if(event.key === 'Backspace') {
        if(document.activeElement === this.get_barcode_input_element()) return
        event.preventDefault()
        this.barcode = this.barcode.slice(0, -1)
        return
      }

      if(event.key.length !== 1) return
      if(document.activeElement === this.get_barcode_input_element()) return
      event.preventDefault()
      this.barcode += event.key
      this.submit_barcode()
    },
    barcode_matches_parcel(barcode){
      return this.order.line_items.some(item => {
        const product = this.get_product(item.uuid)
        return product && product.barcode && barcodes_match(barcode, product.barcode)
      })
    },
    clear_barcode_input(){
      this.barcode = ''
      // Keep Vuetify's internal value and the native input in sync with the
      // model. Clearing only the model can leave stale text in the DOM when
      // the clear happens mid input event or from a timeout.
      const field = this.$refs.barcodeInput
      if(field) field.internalValue = ''
      const inputElement = this.get_barcode_input_element()
      if(inputElement) inputElement.value = ''
      this.$nextTick(() => {
        const element = this.get_barcode_input_element()
        if(element && element.value) element.value = ''
      })
    },
    submit_barcode({ force = false } = {}){
      if(this.barcode_input_timeout) clearTimeout(this.barcode_input_timeout)
      this.barcode_input_timeout = null
      const barcode = normalize_barcode(this.barcode)
      if(!barcode) return
      if(!force && !this.barcode_matches_parcel(barcode)) return
      this.$emit('scan_barcode', { barcode, index: this.index })
      this.clear_barcode_input()
      if(this.uses_barcode_scanner) this.$nextTick(() => this.focus_barcode_input())
    },
    on_barcode_input(barcode){
      this.barcode = barcode || ''
      if(this.barcode_input_timeout) clearTimeout(this.barcode_input_timeout)
      this.barcode_input_timeout = null

      // Submit right away once the value matches an item in this parcel
      if(this.barcode_matches_parcel(this.barcode)){
        this.submit_barcode()
        return
      }

      // Hardware scanners may not send an Enter/Tab suffix. Scanners type the
      // whole barcode within milliseconds, so once input stops we force submit
      // whatever was scanned and let the parent handle mismatches.
      if(!normalize_barcode(this.barcode)) return
      this.barcode_input_timeout = setTimeout(() => {
        this.barcode_input_timeout = null
        this.submit_barcode({ force: true })
      }, 250)
    },
    on_scanned_barcode(barcode){
      this.$emit('scan_barcode', { barcode: normalize_barcode(barcode), index: this.index })
      this.clear_barcode_input()
      if(this.uses_barcode_scanner) this.$nextTick(() => this.focus_barcode_input())
    },
    async prompt_manual_scan(item){
      if(item.is_scanned) return
      const productTitle = this.get_product_title(item.uuid) || item.name
      this.manual_scan_prompt_open = true
      const { ok } = await this.$prompt({
        text: `Barcode scanning is required for "${productTitle}". If the physical barcode is missing or faulty, you can mark it as scanned manually.`,
        buttons: [
          {
            value: { ok: true },
            text: 'Mark as scanned'
          },
          {
            value: { ok: false },
            text: 'Cancel'
          },
        ]
      }).finally(() => {
        this.manual_scan_prompt_open = false
      })
      if(!ok) return
      const item_index = this.order.line_items.findIndex(line_item => line_item === item)
      if(item_index === -1) return
      this.$emit('mark_item_scanned', { index: this.index, item_index })
    },
    set_method(req){
      this.loading_manual_shipment = true
      this.$emit('update', {...req, index: this.index})
    },
    async openPrint(file_ref){
      const url = await getDownloadURL(storageRef(getStorage(), file_ref))
      window.open(url, 'new')
    },
    async printWithDialog(file_ref){
      const url = await getDownloadURL(storageRef(getStorage(), file_ref))
      const xhr = new XMLHttpRequest();
      xhr.responseType = 'blob';
      xhr.onload = () => {
        const blob = xhr.response;
        print({ printable: URL.createObjectURL(blob) })
      };
      xhr.open('GET', url);
      xhr.send();
      print({ printable: url })
    },
    can_delete_label(label){
      return is_label_deletable({
        carrier: this.carrier_account,
        tracking_number: label.tracking_number,
        created_at: label.created_at,
      })
    },
    async delete_label(label){
      const { ok } = await this.$prompt({
        text: `Delete label ${label.tracking_number}? The shipment will be cancelled at the carrier and the label removed from this order.`,
        buttons: [
          { value: { ok: true }, text: 'Delete label' },
          { value: { ok: false }, text: 'Cancel' },
        ]
      })
      if(!ok) return
      this.deleting_label = true
      try {
        await this.$store.dispatch('order/delete_label', { index: this.index, tracking_number: label.tracking_number })
      } finally {
        this.deleting_label = false
      }
    },
    sendToPrint({ pdf_ref, description, media }){ 
      this.$store.dispatch('warehouse/submit_print_job', { 
        pdf_ref, 
        description, 
        media, 
        carrier_id: this.order.sf_data.shipping_service.sf_carrier_account_id 
      })
    },
  },
  mounted(){
    window.addEventListener('keydown', this.on_hardware_scanner_keydown)
  },
  beforeDestroy(){
    console.log('on destroy')
    window.removeEventListener('keydown', this.on_hardware_scanner_keydown)
    if(this.timer) clearInterval(this.timer)
    if(this.scanner_health_timeout) clearTimeout(this.scanner_health_timeout)
    if(this.barcode_input_timeout) clearTimeout(this.barcode_input_timeout)
  }
}
</script>

<style lang="scss" scoped>
.item__title {
  white-space: normal !important;
}

.parcel-card-container {
  max-width: 600px;
  margin: 0 auto;
}

.parcel-card {
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  overflow: hidden;
  background-color: white;
  
  &.active-card {
    transform: translateY(-5px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1) !important;
  }
}

.parcel-header {
  background-color: #f9f9fa;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  padding: 16px;
  position: relative;
}

.reload-button {
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  z-index: 10;
  background-color: rgba(255, 255, 255, 0.9);
  transition: all 0.2s ease;
  
  &:hover {
    background-color: rgba(255, 255, 255, 1);
    transform: translateY(-50%) rotate(180deg);
  }
}

.parcel-title {
  overflow-x: auto;
  white-space: nowrap;
  max-width: 100%;
  display: block;
}

.parcel-content {
  padding: 16px;
}

.scanner-section {
  padding: 16px;
  background-color: rgba(0, 0, 0, 0.02);
  animation: fadeIn 0.4s ease-out;
}

.barcode-scanner {
  width: 100%;
  max-width: 300px;
  margin: 0 auto 16px;
  border-radius: 8px;
  overflow: hidden;
}

.barcode-input {
  max-width: 300px;
  margin: 0 auto;
}

.labels-section {
  animation: fadeIn 0.4s ease-out;
  display: flex;
  gap: 1em;
  align-items: center;
}

.labels-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
}

.label-btn {
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
  }
}

.tracking-number {
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.signature-note {
  display: flex;
  align-items: center;
  margin-top: 8px;
  color: rgba(0, 0, 0, 0.6);
}

.packing-instructions-card,
.items-card {
  // background-color: rgba(0, 0, 0, 0.02);
  transition: all 0.2s ease;
  
  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05) !important;
  }
}

.special-instructions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.instruction-chip {
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
  }
}

.parcel-actions {
  padding: 16px;
}

.action-button {
  min-width: 160px;
}

.skip-button {
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
  }
}

/* Animations */
.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
}

.slide-enter-to, 
.slide-leave {
  max-height: 300px;
  overflow: hidden;
}

.slide-enter, 
.slide-leave-to {
  overflow: hidden;
  max-height: 0;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Mobile-specific styles */
@media (max-width: 600px) {
  .parcel-header {
    padding: 12px;
  }
  
  .parcel-content {
    padding: 12px;
  }
  
  .parcel-actions {
    padding: 12px;
  }
  
  .parcel-title {
    font-size: 1.1rem;
  }
}
</style>
